import { useEffect, useState } from "react";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { FileSignature, Printer, Download, Trash2, Eye, X, Sparkles, ShieldAlert } from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" });
};

export function buildLetterHtml(f, lh = {}) {
  const perksList = (f.perks || "")
    .split(/\n|;/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => `<li style="margin:4px 0">${p}</li>`)
    .join("");
  const contact = [lh.phone, lh.email].filter(Boolean).join(" · ");
  const letterhead = (lh.logo_data || lh.address || lh.company_name)
    ? `<table style="width:100%;border-bottom:2px solid #1a1a1a;padding-bottom:12px;margin-bottom:20px"><tr>
        <td style="vertical-align:middle">
          ${lh.logo_data ? `<img src="${lh.logo_data}" alt="logo" style="max-height:64px;max-width:200px"/>` : ""}
        </td>
        <td style="text-align:right;vertical-align:middle;font-size:13px;color:#444;line-height:1.5">
          <b style="font-size:16px;color:#1a1a1a">${lh.company_name || f.company_name || ""}</b><br/>
          ${(lh.address || "").replace(/\n/g, "<br/>")}
          ${contact ? `<br/>${contact}` : ""}
        </td>
      </tr></table>`
    : "";
  return `
  <div style="font-family:Georgia,'Times New Roman',serif;color:#1a1a1a;line-height:1.7;max-width:720px">
    ${letterhead}
    <p style="text-align:right;color:#555">${fmtDate(new Date().toISOString())}</p>
    ${letterhead ? "" : `<h2 style="margin:0 0 4px 0">${f.company_name || "Our Company"}</h2>`}
    <p style="margin:0 0 24px 0;color:#555;font-size:14px">Offer of Employment</p>
    <p>Dear <b>${f.candidate_name}</b>,</p>
    <p>Congratulations — and welcome! 🎉 All of us at <b>${f.company_name || "our company"}</b> are absolutely delighted to offer you the position of <b>${f.job_title}</b>. From the very first conversation, your energy, skill and spark stood out, and we can't wait to see what we'll build together.</p>
    <p>Here's everything you need to know about your offer:</p>
    <ul style="padding-left:20px">
      ${f.employee_id ? `<li style="margin:6px 0"><b>Employee ID No.:</b> ${f.employee_id}</li>` : ""}
      ${f.start_date ? `<li style="margin:6px 0"><b>Start date:</b> ${fmtDate(f.start_date)}</li>` : ""}
      ${f.work_location ? `<li style="margin:6px 0"><b>Work location / type:</b> ${f.work_location}</li>` : ""}
      ${f.reporting_to ? `<li style="margin:6px 0"><b>Reporting to:</b> ${f.reporting_to}</li>` : ""}
      ${f.compensation ? `<li style="margin:6px 0"><b>Compensation:</b> ${f.compensation}</li>` : ""}
    </ul>
    ${perksList ? `<p><b>Perks &amp; benefits you'll enjoy:</b></p><ul style="padding-left:20px">${perksList}</ul>` : ""}
    <p>You'll be joining a team that genuinely loves what it does — people who support each other, celebrate wins together and are building something meaningful every single day. We think you'll fit right in, and honestly, we think you'll make us better.</p>
    ${f.response_deadline ? `<p>To accept this offer, simply sign below and return this letter by <b>${f.response_deadline}</b>. If you have any questions at all, we're just a call or email away.</p>` : `<p>To accept this offer, simply sign below and return this letter. If you have any questions at all, we're just a call or email away.</p>`}
    <p>We're so excited to welcome you aboard, ${(f.candidate_name || "").split(" ")[0]}!</p>
    <p style="margin-top:32px">Warm regards,</p>
    <p style="margin:24px 0 0 0"><b>HR Team</b><br/>${f.company_name || ""}</p>
    <div style="margin-top:48px;border-top:1px solid #999;padding-top:16px">
      <p style="font-size:14px;color:#555;margin-bottom:28px"><b>Acceptance</b> — I, ${f.candidate_name}, happily accept this offer for the position of ${f.job_title}.</p>
      <table style="width:100%;font-size:14px"><tr>
        <td>Signature: ______________________</td>
        <td style="text-align:right">Date: ______________</td>
      </tr></table>
    </div>
  </div>`;
}

function printLetter(html, title) {
  const w = window.open("", "_blank");
  w.document.write(`<html><head><title>${title}</title></head><body style="padding:40px">${html}</body></html>`);
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 400);
}

function downloadLetter(html, title) {
  const blob = new Blob(
    [`<html><head><meta charset="utf-8"><title>${title}</title></head><body>${html}</body></html>`],
    { type: "application/msword" }
  );
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${title}.doc`;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

const EMPTY = {
  candidate_name: "", job_title: "", company_name: "HCIT", employee_id: "",
  start_date: "", work_location: "", reporting_to: "",
  compensation: "", perks: "", response_deadline: "",
};

export default function OfferLetters() {
  const { user } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [history, setHistory] = useState([]);
  const [letterhead, setLetterhead] = useState({});
  const [preview, setPreview] = useState(null); // {title, html}
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const load = () => api.get("/offer-letters").then((r) => setHistory(r.data.filter((x) => (x.letter_type || "offer") === "offer"))).catch((e) => setErr(formatApiError(e)));
  useEffect(() => {
    load();
    api.get("/settings/letterhead").then((r) => {
      setLetterhead(r.data);
      if (r.data.company_name) setForm((f) => ({ ...f, company_name: r.data.company_name }));
    }).catch(() => {});
  }, []);

  const generate = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const html = buildLetterHtml(form, letterhead);
      const r = await api.post("/offer-letters", { ...form, letter_html: html });
      setPreview({ title: `Offer Letter - ${form.candidate_name}`, html, id: r.data.id });
      setForm({ ...EMPTY, company_name: letterhead.company_name || EMPTY.company_name });
      await load();
    } catch (er) { setErr(formatApiError(er)); }
    finally { setBusy(false); }
  };

  const del = async (o) => {
    if (!window.confirm(`Delete offer letter for ${o.candidate_name}?`)) return;
    try { await api.delete(`/offer-letters/${o.id}`); await load(); }
    catch (e) { setErr(formatApiError(e)); }
  };

  if (user?.role === "company_admin") {
    return (
      <div className="mx-auto flex max-w-md flex-col items-center px-4 py-24 text-center">
        <ShieldAlert className="h-12 w-12 text-destructive" />
        <h1 data-testid="offer-access-denied" className="mt-4 font-heading text-2xl font-bold">Access Denied</h1>
        <p className="mt-2 text-sm text-muted-foreground">Offer Letters are not available for Company Admin accounts.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-6 sm:px-6 lg:px-8">
      <header className="mb-6">
        <p className="label-eyebrow text-primary">HR Tools</p>
        <h1 className="mt-1 font-heading text-3xl font-bold tracking-tight">Offer Letters</h1>
        <p className="mt-1 text-sm text-muted-foreground">Generate warm, professional job offer letters — print, download and keep a history. Logo &amp; address come from Settings → Company Letterhead.</p>
      </header>

      {err && <div className="mb-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}

      <div className="grid gap-6 lg:grid-cols-2">
        <form onSubmit={generate} className="sharp-card grid gap-3 p-5 sm:grid-cols-2 self-start">
          <h2 className="sm:col-span-2 flex items-center gap-2 font-heading text-lg font-semibold"><FileSignature className="h-5 w-5 text-primary" /> New offer letter</h2>
          <div>
            <label className="label-eyebrow mb-1.5 block">Candidate name *</label>
            <input data-testid="offer-candidate" required value={form.candidate_name} onChange={set("candidate_name")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Job title *</label>
            <input data-testid="offer-job-title" required value={form.job_title} onChange={set("job_title")} placeholder="e.g. Field Service Engineer" className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Company name *</label>
            <input data-testid="offer-company" required value={form.company_name} onChange={set("company_name")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Employee ID No.</label>
            <input data-testid="offer-employee-id" value={form.employee_id} onChange={set("employee_id")} placeholder="e.g. HCIT-0042" className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Start date</label>
            <input data-testid="offer-start-date" type="date" value={form.start_date} onChange={set("start_date")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Work location / type</label>
            <input data-testid="offer-location" value={form.work_location} onChange={set("work_location")} placeholder="e.g. Office — Mumbai / Hybrid / Remote" className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Reporting to</label>
            <input data-testid="offer-reporting" value={form.reporting_to} onChange={set("reporting_to")} placeholder="e.g. Rajesh Kumar, Service Manager" className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Compensation & schedule</label>
            <input data-testid="offer-compensation" value={form.compensation} onChange={set("compensation")} placeholder="e.g. ₹6,00,000 per year, paid monthly" className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Response deadline</label>
            <input data-testid="offer-deadline" value={form.response_deadline} onChange={set("response_deadline")} placeholder="e.g. within 5 business days" className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div className="sm:col-span-2">
            <label className="label-eyebrow mb-1.5 block">Perks & benefits (one per line)</label>
            <textarea data-testid="offer-perks" value={form.perks} onChange={set("perks")} rows={3} placeholder={"Health insurance for you & family\nLearning budget ₹25,000/yr\n24 PTO days"} className="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div className="sm:col-span-2 flex justify-end">
            <button data-testid="offer-generate" disabled={busy} className="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
              <Sparkles className="h-4 w-4" /> {busy ? "Generating…" : "Generate letter"}
            </button>
          </div>
        </form>

        <div className="sharp-card self-start overflow-hidden">
          <div className="border-b border-border/60 bg-secondary/30 px-5 py-3">
            <h2 className="font-heading text-lg font-semibold">History ({history.length})</h2>
          </div>
          <div className="max-h-[560px] divide-y divide-border/40 overflow-y-auto">
            {history.map((o) => (
              <div key={o.id} data-testid={`offer-row-${o.id}`} className="flex items-center justify-between gap-2 px-5 py-3 hover:bg-secondary/40">
                <div className="min-w-0">
                  <p className="truncate font-medium">{o.candidate_name} — {o.job_title}</p>
                  <p className="text-xs text-muted-foreground">{o.company_name} · {fmtDate(o.created_at)} · by {o.created_by_name || "—"}</p>
                </div>
                <div className="flex shrink-0 gap-1">
                  <button data-testid={`offer-view-${o.id}`} title="View" onClick={() => setPreview({ title: `Offer Letter - ${o.candidate_name}`, html: o.letter_html })} className="rounded-md border border-border/70 p-1.5 text-muted-foreground hover:text-primary"><Eye className="h-3.5 w-3.5" /></button>
                  <button data-testid={`offer-print-${o.id}`} title="Print" onClick={() => printLetter(o.letter_html, `Offer Letter - ${o.candidate_name}`)} className="rounded-md border border-border/70 p-1.5 text-muted-foreground hover:text-primary"><Printer className="h-3.5 w-3.5" /></button>
                  <button data-testid={`offer-download-${o.id}`} title="Download (Word)" onClick={() => downloadLetter(o.letter_html, `Offer Letter - ${o.candidate_name}`)} className="rounded-md border border-border/70 p-1.5 text-muted-foreground hover:text-primary"><Download className="h-3.5 w-3.5" /></button>
                  <button data-testid={`offer-delete-${o.id}`} title="Delete" onClick={() => del(o)} className="rounded-md border border-border/70 p-1.5 text-muted-foreground hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            ))}
            {history.length === 0 && <p className="px-5 py-10 text-center text-xs text-muted-foreground">No offer letters yet — generate your first one.</p>}
          </div>
        </div>
      </div>

      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setPreview(null)}>
          <div className="w-full max-w-3xl sharp-card max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-border/60 px-5 py-3">
              <h3 className="font-heading text-lg font-semibold">{preview.title}</h3>
              <div className="flex gap-2">
                <button data-testid="offer-preview-print" onClick={() => printLetter(preview.html, preview.title)} className="inline-flex items-center gap-1.5 rounded-md border border-border/70 px-3 py-1.5 text-xs font-semibold hover:bg-secondary"><Printer className="h-3.5 w-3.5" /> Print</button>
                <button data-testid="offer-preview-download" onClick={() => downloadLetter(preview.html, preview.title)} className="inline-flex items-center gap-1.5 rounded-md border border-border/70 px-3 py-1.5 text-xs font-semibold hover:bg-secondary"><Download className="h-3.5 w-3.5" /> Download</button>
                <button data-testid="offer-preview-close" onClick={() => setPreview(null)} className="rounded-md border border-border/70 p-1.5"><X className="h-4 w-4" /></button>
              </div>
            </div>
            <div className="overflow-y-auto bg-white p-8" dangerouslySetInnerHTML={{ __html: preview.html }} />
          </div>
        </div>
      )}
    </div>
  );
}
